import React from 'react';
import { Button } from '@/components/ui/button';
import { Lock } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BlurredPrivateDataProps {
  /** The placeholder content to show blurred */
  placeholderContent: React.ReactNode;
  /** The field name for the button label (e.g., "Price", "Location") */
  fieldName: string;
  /** Callback when user clicks to request access */
  onRequestInfo?: () => void;
  /** Optional className for the container */
  className?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to show inline (in text flow) or as a block */
  inline?: boolean;
}

/**
 * BlurredPrivateData - Displays hidden/private data with a blur effect
 * and an overlay button to request access.
 * 
 * Used when sellers have privacy settings enabled for specific fields.
 * The layout stays exactly the same - only the content becomes blurred.
 */
export const BlurredPrivateData: React.FC<BlurredPrivateDataProps> = ({
  placeholderContent,
  fieldName,
  onRequestInfo,
  className,
  size = 'md',
  inline = false,
}) => {
  const sizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  const buttonSizeClasses = {
    sm: 'text-[10px] px-2 py-0.5 h-5',
    md: 'text-xs px-2.5 py-1 h-6',
    lg: 'text-sm px-3 py-1.5 h-7',
  };

  if (inline) {
    return (
      <span className={cn("inline-flex items-center gap-1.5", className)}>
        <span className="blur-sm select-none pointer-events-none opacity-60">
          {placeholderContent}
        </span>
        <Lock className={cn(
          "text-muted-foreground flex-shrink-0",
          size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-4 h-4' : 'w-3.5 h-3.5'
        )} />
        {onRequestInfo && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onRequestInfo();
            }}
            className={cn(
              "h-auto p-0 text-primary hover:text-primary/80 underline underline-offset-2",
              sizeClasses[size]
            )}
          >
            Request
          </Button>
        )}
      </span>
    );
  }

  return (
    <div className={cn("relative group", className)}>
      {/* Blurred placeholder content - maintains exact layout */}
      <div className="blur-sm select-none pointer-events-none opacity-60">
        {placeholderContent}
      </div>

      {/* Overlay with lock icon and request button */}
      <div className="absolute inset-0 flex items-center justify-center bg-background/30 backdrop-blur-[1px] rounded">
        <div className="flex items-center gap-2">
          <Lock className={cn(
            "text-muted-foreground",
            size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4'
          )} />
          {onRequestInfo && (
            <Button
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onRequestInfo();
              }}
              className={cn(
                "font-medium shadow-sm",
                buttonSizeClasses[size]
              )}
            >
              Request {fieldName}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlurredPrivateData;
